import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    IconButton,
    Tooltip,
    TextField,
} from '@mui/material';
import { DataGrid, GridToolbar } from '@mui/x-data-grid';
import { useTheme } from '@mui/material';
import { tokens } from '../../theme';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import RemoveCircleIcon from '@mui/icons-material/RemoveCircle';
import axios from 'axios';
import { MenuItem } from '@mui/material';
const API_ENDPOINTS = {
    invoices: 'https://figazuresql.swancreate.com/api/swa/FINANCE_Invoices',
    invoiceCategories: 'https://figazuresql.swancreate.com/api/swa/FINANCE_Categories',
};

const Invoices = () => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode);
    const [invoiceData, setInvoiceData] = useState([]);
    const [invoiceCategories, setInvoiceCategories] = useState([]);
    const [editingRow, setEditingRow] = useState(null);

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [invoicesRes, categoriesRes] = await Promise.all([
                    axios.get(API_ENDPOINTS.invoices),
                    axios.get(API_ENDPOINTS.invoiceCategories),
                ]);
                setInvoiceData(invoicesRes.data);
                setInvoiceCategories(categoriesRes.data);
            } catch (error) {
                console.error('Error fetching data:', error);
            }
        };

        fetchData();
    }, []);

    const handleAddNewRow = () => {
        const newInvoice = {
            InvoiceID: Date.now().toString(),
            InvoiceNumber: '',
            InvoiceDate: '',
            TotalAmount: '',
            Description: '',
            DateDue: '',
            Category: '',
        };
        setInvoiceData([...invoiceData, newInvoice]);
        setEditingRow(newInvoice.InvoiceID);
    };

    const handleSaveRow = async (row) => {
        try {
            if (!row.InvoiceID) {
                console.error('Row ID missing for saving.');
                return;
            }

            // Save to the API
            await axios.put(`${API_ENDPOINTS.invoices}/${row.InvoiceID}`, row);
            setEditingRow(null);
        } catch (error) {
            console.error('Error saving row:', error);
        }
    };

    const handleRemoveRow = async (id) => {
        try {
            // Delete from the API
            await axios.delete(`${API_ENDPOINTS.invoices}/${id}`);
            setInvoiceData(invoiceData.filter((invoice) => invoice.InvoiceID !== id));
        } catch (error) {
            console.error('Error removing row:', error);
        }
    };

    const handleInputChange = (event, id, field) => {
        const updatedData = invoiceData.map((invoice) =>
            invoice.InvoiceID === id ? { ...invoice, [field]: event.target.value } : invoice
        );
        setInvoiceData(updatedData);
    };

    const columns = [
        {
            field: 'InvoiceNumber',
            headerName: 'Invoice Number',
            flex: 1,
            renderCell: (params) =>
                editingRow === params.row.InvoiceID ? (
                    <TextField
                        value={params.row.InvoiceNumber || ''}
                        onChange={(event) => handleInputChange(event, params.row.InvoiceID, 'InvoiceNumber')}
                        size="small"
                    />
                ) : (
                    params.row.InvoiceNumber
                ),
        },
        {
            field: 'TotalAmount',
            headerName: 'Total Amount',
            flex: 1,
            type: 'number',
            renderCell: (params) =>
                editingRow === params.row.InvoiceID ? (
                    <TextField
                        value={params.row.TotalAmount || ''}
                        onChange={(event) => handleInputChange(event, params.row.InvoiceID, 'TotalAmount')}
                        size="small"
                        type="number"
                    />
                ) : (
                    params.row.TotalAmount
                ),
        },
        {
            field: 'Category',
            headerName: 'Category',
            flex: 1,
            renderCell: (params) =>
                editingRow === params.row.InvoiceID ? (
                    <TextField
                        select
                        value={params.row.Category || ''}
                        onChange={(event) => handleInputChange(event, params.row.InvoiceID, 'Category')}
                        size="small"
                        fullWidth
                    >
                        {invoiceCategories.map((category) => (
                            <MenuItem key={category.InvoiceCategoryID} value={category.CategoryName}>
                                {category.CategoryName}
                            </MenuItem>
                        ))}
                    </TextField>
                ) : (
                    params.row.Category
                ),
        },
        {
            field: 'actions',
            headerName: 'Actions',
            flex: 0.5,
            renderCell: (params) => (
                <>
                    {editingRow === params.row.InvoiceID ? (
                        <Tooltip title="Save">
                            <IconButton onClick={() => handleSaveRow(params.row)}>
                                <SaveIcon />
                            </IconButton>
                        </Tooltip>
                    ) : (
                        <Tooltip title="Edit">
                            <IconButton onClick={() => setEditingRow(params.row.InvoiceID)}>
                                <EditIcon />
                            </IconButton>
                        </Tooltip>
                    )}
                    <Tooltip title="Remove">
                        <IconButton color="error" onClick={() => handleRemoveRow(params.row.InvoiceID)}>
                            <RemoveCircleIcon />
                        </IconButton>
                    </Tooltip>
                </>
            ),
        },
    ];

    return (
        <Box m="20px">
            <Typography variant="h4" sx={{ color: colors.grey[100] }}>
                Invoice List
            </Typography>

            <Button
                startIcon={<AddIcon />}
                onClick={handleAddNewRow}
                sx={{ mt: 2, mb: 1 }}
                variant="contained"
                color="primary"
            >
                Add New Invoice
            </Button>

            <Box height="70vh">
                <DataGrid
                    rows={invoiceData}
                    columns={columns}
                    components={{ Toolbar: GridToolbar }}
                    getRowId={(row) => row.InvoiceID}
                    disableSelectionOnClick
                    sx={{
                        '& .MuiDataGrid-root': { border: 'none' },
                        '& .MuiDataGrid-cell': { borderBottom: 'none' },
                        '& .MuiDataGrid-columnHeaders': {
                            backgroundColor: colors.blueAccent[700],
                            borderBottom: 'none',
                        },
                        '& .MuiDataGrid-virtualScroller': {
                            backgroundColor: colors.primary[400],
                        },
                        '& .MuiDataGrid-footerContainer': {
                            borderTop: 'none',
                            backgroundColor: colors.blueAccent[700],
                        },
                    }}
                />
            </Box>
        </Box>
    );
};

export default Invoices;
